import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store";
import { fetchCategoriesByBalance, resetCategoriesByBalance } from "../../../../store/slices/categoriesByBalance";
import Input from "../../../ui/common/Input";
import Select from "../../../ui/select/Select";
import Button from "../../../ui/common/Button";
import { EditIcon, DeleteIcon, PlusIcon, SaveIcon, CloseIcon } from "../../../../icons/Icons";
import Api from "../../../../store/api/Api";
import "./ChannelOptimalisationModal.scss";

interface Benchmark {
  id?: string;
  brandId?: string;
  balance: string;
  category: string;
  path: string;
  min: number;
  max: number;
  timestamp?: string;
}

interface ChannelOptimalisationModalProps {
  brandId: string;
  onClose: () => void;
  hideActions?: boolean;
}

const ChannelOptimalisationModal = ({ brandId, onClose, hideActions = false }: ChannelOptimalisationModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const emptyBenchmark: Benchmark = {
    balance: "",
    category: "",
    path: "",
    min: 0,
    max: 100
  };

  const balanceDropdownOptions = ["user_needs", "video", "sentiment", "access_level", "readability"];

  const [benchmarkEditIndex, setBenchmarkEditIndex] = useState<number | null>(null);
  const [benchmarkEditData, setBenchmarkEditData] = useState<Benchmark>(emptyBenchmark);
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBalance, setSelectedBalance] = useState<string>(searchParams.get("balance") || "All Balances");
  const [selectedPath, setSelectedPath] = useState<string>(searchParams.get("path") || "All Paths");
  const api = new Api();
  const benchmarkBodyRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  
  const categories = useSelector((state: any) => state.categoriesByBalance.data);
  const loadingCategories = useSelector((state: any) => state.categoriesByBalance.isLoading);

  const balanceOptions = useMemo(() => {
    const balances = benchmarks.map(b => b.balance).filter(Boolean);
    const uniqueBalances = Array.from(new Set(balances)).sort();
    return ["All Balances", ...uniqueBalances];
  }, [benchmarks]);

  const pathOptions = useMemo(() => {
    const paths = benchmarks.map(b => b.path).filter(Boolean);
    const uniquePaths = Array.from(new Set(paths)).sort();
    return ["All Paths", ...uniquePaths];
  }, [benchmarks]);

  const filteredBenchmarks = useMemo(() => {
    let filtered = benchmarks;
    
    if (selectedBalance !== "All Balances") {
      filtered = filtered.filter(b => b.balance === selectedBalance);
    }
    
    if (selectedPath !== "All Paths") {
      filtered = filtered.filter(b => b.path === selectedPath);
    }
    
    return filtered;
  }, [benchmarks, selectedBalance, selectedPath]);

  useEffect(() => {
    fetchBenchmarks();
  }, [brandId]);

  useEffect(() => {
    if (benchmarkEditIndex !== null && benchmarkEditData.balance) {
      dispatch(fetchCategoriesByBalance(brandId, benchmarkEditData.balance));
    } else {
      dispatch(resetCategoriesByBalance());
    }
  }, [benchmarkEditData.balance, benchmarkEditIndex, brandId, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams();
    
    if (selectedBalance !== "All Balances") {
      params.set("balance", selectedBalance);
    }
    
    if (selectedPath !== "All Paths") {
      params.set("path", selectedPath);
    }
    
    setSearchParams(params, { replace: true });
  }, [selectedBalance, selectedPath, setSearchParams]);

  const fetchBenchmarks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getBenchmarks(brandId);
      const benchmarksData = Array.isArray(response) ? response : (response.data || []);
      setBenchmarks(benchmarksData);
    } catch (err: any) {
      setError(err.message || "Failed to fetch benchmarks");
      console.error("Error fetching benchmarks:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: any, field: keyof Benchmark) => {
    setBenchmarkEditData({
      ...benchmarkEditData,
      [field]: value,
    });
  };

  const handleBenchmarkSave = async () => {
    if (!benchmarkEditData.balance || !benchmarkEditData.category) {
      setError("Balance and Category are required fields");
      return;
    }

    if (benchmarkEditData.min > benchmarkEditData.max) {
      setError("Minimum value cannot be greater than maximum value");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        balance: benchmarkEditData.balance,
        category: benchmarkEditData.category,
        path: benchmarkEditData.path,
        min: benchmarkEditData.min,
        max: benchmarkEditData.max
      };

      const existingBenchmark = benchmarks[benchmarkEditIndex!];
      
      if (existingBenchmark?.id) {
        await api.postUpdateBenchmark(existingBenchmark.id, payload);
      } else {
        // add brandId to payload
        payload['brandId'] = brandId;
        await api.postCreateBenchmark(payload);
      }
      
      // wait for 1 second
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchBenchmarks();
      setBenchmarkEditIndex(null);
    } catch (err: any) {
      setError(err.message || "Failed to save benchmark");
      console.error("Error saving benchmark:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBenchmarkCancel = () => {
    if (benchmarkEditData.balance === "" && benchmarkEditData.category === "") {
      const updatedBenchmarks = benchmarks.filter((_, key) => key !== benchmarkEditIndex);
      setBenchmarks(updatedBenchmarks);
    }
    setBenchmarkEditIndex(null);
    setError(null);
  };

  const handleBenchmarkDelete = async (key: number) => {
    const benchmark = benchmarks[key];
    
    const confirmed = window.confirm("Are you sure you want to delete this benchmark?");
    if (!confirmed) {
      return;
    }
    
    if (!benchmark?.id) {
      const updatedBenchmarks = benchmarks.filter((_, index) => index !== key);
      setBenchmarks(updatedBenchmarks);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.deleteBenchmark(benchmark.id);
      // wait for 1 second
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchBenchmarks();
    } catch (err: any) {
      setError(err.message || "Failed to delete benchmark");
      console.error("Error deleting benchmark:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBenchmarkNew = () => {
    const updatedBenchmarks = [...benchmarks, emptyBenchmark];
    setBenchmarks(updatedBenchmarks);
    setBenchmarkEditIndex(benchmarks.length);
    setBenchmarkEditData(emptyBenchmark);
    
    setTimeout(() => {
      if (benchmarkBodyRef.current) {
        benchmarkBodyRef.current.scrollTop = benchmarkBodyRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleSave = () => {
    onClose();
  };

  return (
    <div className={`co-modal-wrapper ${hideActions ? "full-width" : ""}`}>
      <h3>Channel Optimisation - Benchmarks</h3>
      <p className="co-description">
        Manage benchmarks for channel optimization. Each benchmark defines target ranges for content categories.
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="filter-container">
        <div className="filter-item">
          <label htmlFor="balance-filter">Filter by Balance:</label>
          <Select
            options={balanceOptions}
            selectedOption={selectedBalance}
            setSelectedOption={setSelectedBalance}
            fullWidth={false}
          />
        </div>
        <div className="filter-item">
          <label htmlFor="path-filter">Filter by Path:</label>
          <Select
            options={pathOptions}
            selectedOption={selectedPath}
            setSelectedOption={setSelectedPath}
            fullWidth={false}
          />
        </div>
      </div>

      <div className="benchmarks-container">
        <div className="benchmark-header">
          <div className="benchmark-balance">
            <p>Balance (Main Category)</p>
          </div>
          <div className="benchmark-category">
            <p>Category (Subcategory)</p>
          </div>
          <div className="benchmark-path">
            <p>Path <em>(* all paths)</em></p>
          </div>
          <div className="benchmark-range">
            <p>Range (Min-Max %)</p>
          </div>
          <div className="benchmark-buttons"></div>
        </div>
        <div className="benchmark-body" ref={benchmarkBodyRef}>
          {loading ? (
            <div className="loading-message">Loading benchmarks...</div>
          ) : filteredBenchmarks.length === 0 ? (
            <div className="empty-message">{selectedBalance === "All Balances" ? "No benchmarks configured. Click + to add one." : `No benchmarks found for balance: ${selectedBalance}`}</div>
          ) : (
            filteredBenchmarks.map((benchmark, key) => (
              <div className="benchmark" key={key}>
                <div 
                  className="benchmark-balance"
                  onClick={() => {
                    if (benchmarkEditIndex === key) {
                      setTimeout(() => {
                        const benchmarkElement = benchmarkBodyRef.current?.children[key] as HTMLElement;
                        if (benchmarkElement && benchmarkBodyRef.current) {
                          const elementTop = benchmarkElement.offsetTop;
                          const containerHeight = benchmarkBodyRef.current.clientHeight;
                          const scrollPosition = elementTop - containerHeight + benchmarkElement.clientHeight + 200;
                          benchmarkBodyRef.current.scrollTop = Math.max(0, scrollPosition);
                        }
                      }, 50);
                    }
                  }}
                >
                  {benchmarkEditIndex === key ? (
                    <Select
                      options={balanceDropdownOptions}
                      selectedOption={benchmarkEditData?.balance || balanceDropdownOptions[0]}
                      setSelectedOption={(value) => handleInputChange(value, "balance")}
                      fullWidth={true}
                    />
                  ) : (
                    <p>{benchmark.balance}</p>
                  )}
                </div>
                <div className="benchmark-category">
                  {benchmarkEditIndex === key ? (
                    loadingCategories ? (
                      <p>Loading categories...</p>
                    ) : categories.length > 0 ? (
                      <Select
                        options={categories}
                        selectedOption={benchmarkEditData?.category || categories[0]}
                        setSelectedOption={(value) => handleInputChange(value, "category")}
                        fullWidth={true}
                      />
                    ) : (
                      <Input
                        type="text"
                        value={benchmarkEditData?.category}
                        onChange={(e) => handleInputChange(e.target.value, "category")}
                        placeholder="e.g., hou_me_op_de_hoogte"
                      />
                    )
                  ) : (
                    <p>{benchmark.category}</p>
                  )}
                </div>
                <div className="benchmark-path">
                  {benchmarkEditIndex === key ? (
                    <Input
                      type="text"
                      value={benchmarkEditData?.path}
                      onChange={(e) => handleInputChange(e.target.value, "path")}
                      placeholder="* for all paths or /path/to/page"
                    />
                  ) : (
                    <p>{benchmark.path}</p>
                  )}
                </div>
                <div className="benchmark-range">
                  {benchmarkEditIndex === key ? (
                    <div className="range-slider-container">
                      <div className="range-inputs">
                        <div className="range-input-group">
                          <label>Min:</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={benchmarkEditData.min}
                            onChange={(e) => handleInputChange(parseInt(e.target.value) || 0, "min")}
                            className="range-number-input"
                          />
                          <span>%</span>
                        </div>
                        <div className="range-input-group">
                          <label>Max:</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={benchmarkEditData.max}
                            onChange={(e) => handleInputChange(parseInt(e.target.value) || 0, "max")}
                            className="range-number-input"
                          />
                          <span>%</span>
                        </div>
                      </div>
                      <div className="range-sliders">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={benchmarkEditData.min}
                          onChange={(e) => handleInputChange(parseInt(e.target.value), "min")}
                          className="range-slider range-slider-min"
                        />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={benchmarkEditData.max}
                          onChange={(e) => handleInputChange(parseInt(e.target.value), "max")}
                          className="range-slider range-slider-max"
                        />
                      </div>
                    </div>
                  ) : (
                    <p>{benchmark.min}% - {benchmark.max}%</p>
                  )}
                </div>
                <div className="benchmark-buttons">
                  <Button
                    variant="success"
                    size="small"
                    title="Save"
                    onClick={handleBenchmarkSave}
                    style={{ display: benchmarkEditIndex === key ? "" : "none" }}
                  >
                    <SaveIcon />
                  </Button>

                  <Button
                    variant="warning"
                    size="small"
                    title="Cancel"
                    onClick={handleBenchmarkCancel}
                    style={{
                      marginLeft: "10px",
                      marginRight: "10px",
                      display: benchmarkEditIndex === key ? "" : "none",
                    }}
                  >
                    <CloseIcon />
                  </Button>

                  <Button
                    variant="warning"
                    size="small"
                    title="Edit"
                    onClick={() => {
                      setBenchmarkEditData(benchmark);
                      setBenchmarkEditIndex(key);
                      
                      setTimeout(() => {
                        const benchmarkElement = benchmarkBodyRef.current?.children[key] as HTMLElement;
                        if (benchmarkElement && benchmarkBodyRef.current) {
                          const elementTop = benchmarkElement.offsetTop;
                          const containerHeight = benchmarkBodyRef.current.clientHeight;
                          const scrollPosition = elementTop - containerHeight + benchmarkElement.clientHeight + 200;
                          benchmarkBodyRef.current.scrollTop = Math.max(0, scrollPosition);
                        }
                      }, 100);
                    }}
                    style={{ display: benchmarkEditIndex === key ? "none" : "" }}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    title="Delete"
                    style={{
                      marginLeft: "10px",
                      marginRight: "10px",
                      display: benchmarkEditIndex === key ? "none" : "",
                    }}
                    onClick={() => handleBenchmarkDelete(key)}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="benchmark-footer">
          <Button
            variant="success"
            size="small"
            title={benchmarkEditIndex !== null ? "First save or cancel editing" : "Add new benchmark"}
            style={{ marginLeft: "46%", marginRight: "47%" }}
            onClick={handleBenchmarkNew}
            disabled={benchmarkEditIndex !== null}
          >
            <PlusIcon />
          </Button>
        </div>
      </div>

      {!hideActions && (
        <div className="sub-modal-actions">
          <Button
            variant="danger"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => {
              handleSave();
              onClose();
            }}
          >
            OK
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChannelOptimalisationModal;
